import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  Button,
  ButtonVariant,
  Typography,
  TypographyVariant,
} from "../../../../components/ui-kit";
import {
  NotificationTypes,
  useNotification,
} from "../../../../hooks/useNotification";
import api from "../../../../util/api";
import { getApiErrorMessageWithForbiddenFallback } from "../../../../util/apiErrorMessage";
import {
  WalletFeeFenaTariffEditor,
  normalizeWalletFenaTariffForForm,
  type FenaTariffFormState,
} from "../../billing/components/WalletFeeFenaTariffEditor";
import {
  WalletFeeSettlementBankFields,
  bankDetailsLookMasked,
  bankFormIsBlank,
  emptySettlementBank,
  mapApiBankToForm,
  validateSettlementBankForm,
  type SettlementBankForm,
} from "../../billing/components/WalletFeeSettlementBankFields";
import {
  WALLET_FEE_ADMIN_PROVIDERS,
  WALLET_PROVIDER_FIRE,
  walletFeeAdminProviderLabel,
} from "../../billing/components/walletFeeFormConstants";
import {
  WalletMinimumBalanceFields,
  companyMinimumBalanceModeFromApi,
  companyMinimumPoundsFromApi,
  minimumWalletBalanceFromPoundsInput,
  type CompanyMinimumBalanceMode,
} from "../../billing/components/WalletMinimumBalanceFields";

const Card = styled.div`
  margin-top: 20px;
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 10px;
  padding: 20px;
`;

const Actions = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 10px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 12px 0;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ProviderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
`;

const ProviderSelectLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
`;

const ProviderSelect = styled.select`
  min-width: 200px;
  height: 36px;
  border-radius: 5px;
  border: 1px solid #dbe3eb;
  padding: 0 12px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  color: #0e233e;
  background: #fff;
  outline: none;
  &:focus {
    border-color: #027aff;
    box-shadow: 0 0 0 3px rgba(2, 120, 255, 0.153);
  }
`;

const emptyBank = emptySettlementBank;

function applyBankFromApi(
  raw: SettlementBankForm | null | undefined,
  setWalletBank: (b: SettlementBankForm) => void,
  setWalletBankMasked: (m: boolean) => void
) {
  const mapped = mapApiBankToForm(raw);
  if (
    mapped &&
    (mapped.accountName || mapped.sortCode || mapped.accountNumber)
  ) {
    setWalletBank(mapped);
    setWalletBankMasked(bankDetailsLookMasked(mapped));
  } else {
    setWalletBank(emptyBank());
    setWalletBankMasked(false);
  }
}

const CompanyWalletFeeSettings: React.FC<{ companyId?: string }> = ({
  companyId,
}) => {
  const { showNotification } = useNotification();
  const [selectedProvider, setSelectedProvider] =
    useState<string>(WALLET_PROVIDER_FIRE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [walletTariff, setWalletTariff] = useState<FenaTariffFormState>({});
  const [walletBank, setWalletBank] = useState<SettlementBankForm>(emptyBank);
  const [walletBankMasked, setWalletBankMasked] = useState(false);
  const [minimumBalanceMode, setMinimumBalanceMode] =
    useState<CompanyMinimumBalanceMode>("inherit");
  const [minimumBalancePounds, setMinimumBalancePounds] = useState("");

  useEffect(() => {
    if (!companyId) return;
    const load = async () => {
      try {
        setLoading(true);
        const o = (await api.get(
          `/billing/wallet-fees/companies/${companyId}/overrides/${selectedProvider}`
        )) as Record<string, unknown> | null;

        if (!o) {
          setWalletTariff({});
          setWalletBank(emptyBank());
          setWalletBankMasked(false);
          setMinimumBalanceMode("inherit");
          setMinimumBalancePounds("");
          return;
        }

        setWalletTariff(normalizeWalletFenaTariffForForm(o.fenaTariff));
        setMinimumBalanceMode(
          companyMinimumBalanceModeFromApi(
            o.minimumWalletBalance as string | null | undefined,
            true
          )
        );
        setMinimumBalancePounds(
          companyMinimumPoundsFromApi(
            o.minimumWalletBalance as string | null | undefined
          )
        );
        const raw = o.fenaFeeSettlementBank as SettlementBankForm | null;
        applyBankFromApi(raw, setWalletBank, setWalletBankMasked);
      } catch (e: unknown) {
        showNotification(
          getApiErrorMessageWithForbiddenFallback(
            e,
            "Could not load wallet fee override for this company"
          ),
          NotificationTypes.WARNING
        );
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [companyId, selectedProvider]);

  const saveWalletOverride = async () => {
    if (!companyId) return;
    try {
      setSaving(true);
      const tariffPayload: FenaTariffFormState = { ...walletTariff };
      if (
        tariffPayload.transactionsInterimBillingInterval === "" ||
        tariffPayload.transactionsInterimBillingInterval === undefined
      ) {
        delete tariffPayload.transactionsInterimBillingInterval;
      }
      const body: Record<string, unknown> = { fenaTariff: tariffPayload };
      if (!walletBankMasked) {
        if (bankFormIsBlank(walletBank)) {
          body.fenaFeeSettlementBank = null;
        } else {
          const validationError = validateSettlementBankForm(walletBank);
          if (validationError) {
            showNotification(validationError, NotificationTypes.WARNING);
            return;
          }
          body.fenaFeeSettlementBank = walletBank;
        }
      }
      if (minimumBalanceMode === "inherit") {
        body.inheritMinimumWalletBalance = true;
      } else if (minimumBalanceMode === "none") {
        body.minimumWalletBalance = null;
      } else {
        const amount =
          minimumWalletBalanceFromPoundsInput(minimumBalancePounds);
        if (amount == null) {
          showNotification(
            "Enter a valid minimum balance amount or choose another mode",
            NotificationTypes.WARNING
          );
          return;
        }
        body.minimumWalletBalance = amount;
      }
      const saved = (await api.patch(
        `/billing/wallet-fees/companies/${companyId}/overrides/${selectedProvider}`,
        body
      )) as Record<string, unknown>;

      setWalletTariff(normalizeWalletFenaTariffForForm(saved.fenaTariff));
      setMinimumBalanceMode(
        companyMinimumBalanceModeFromApi(
          saved.minimumWalletBalance as string | null | undefined,
          true
        )
      );
      setMinimumBalancePounds(
        companyMinimumPoundsFromApi(
          saved.minimumWalletBalance as string | null | undefined
        )
      );
      const raw = saved.fenaFeeSettlementBank as SettlementBankForm | null;
      applyBankFromApi(raw, setWalletBank, setWalletBankMasked);
      showNotification("Wallet fee override saved", NotificationTypes.SUCCESS);
    } catch (e: unknown) {
      showNotification(
        getApiErrorMessageWithForbiddenFallback(e, "Save failed"),
        NotificationTypes.DANGER
      );
    } finally {
      setSaving(false);
    }
  };

  if (!companyId) {
    return (
      <Typography variant={TypographyVariant.BODY1}>
        Company id is missing.
      </Typography>
    );
  }

  if (loading) {
    return (
      <Typography variant={TypographyVariant.BODY1}>
        Loading wallet fee settings...
      </Typography>
    );
  }

  return (
    <Card>
      <SectionTitle>
        Wallet fees ({walletFeeAdminProviderLabel(selectedProvider)})
      </SectionTitle>
      <ProviderRow>
        <ProviderSelectLabel>Wallet provider</ProviderSelectLabel>
        <ProviderSelect
          aria-label="Wallet provider for company fee override"
          value={selectedProvider}
          onChange={(e) => setSelectedProvider(e.target.value)}
        >
          {WALLET_FEE_ADMIN_PROVIDERS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </ProviderSelect>
      </ProviderRow>
      <Typography variant={TypographyVariant.BODY7} color="#6b7280">
        Optional partial tariff and settlement bank override for this company
        and provider. Leave settlement bank empty to use the global default.
      </Typography>

      <div style={{ marginTop: 12 }}>
        <WalletFeeFenaTariffEditor
          value={walletTariff}
          onChange={setWalletTariff}
          transactionsIntervalMode="company"
        />
      </div>

      <WalletMinimumBalanceFields
        mode="company"
        minimumMode={minimumBalanceMode}
        onMinimumModeChange={setMinimumBalanceMode}
        poundsValue={minimumBalancePounds}
        onPoundsValueChange={setMinimumBalancePounds}
      />

      <Typography
        variant={TypographyVariant.BODY7}
        style={{ color: "#6b7280", margin: "16px 0 8px" }}
      >
        Custom settlement bank (optional). Include beneficiary postal address
        when setting a bank (required for ISX GBP fee charges).
      </Typography>
      <WalletFeeSettlementBankFields
        value={walletBank}
        onChange={(next) => setWalletBank(next ?? emptyBank())}
        disabled={walletBankMasked}
        showClearCustom={!walletBankMasked}
        onClearCustom={() => setWalletBank(emptyBank())}
      />
      <Actions>
        <Button
          variant={ButtonVariant.CONTAINED}
          type="button"
          disabled={saving}
          onClick={() => void saveWalletOverride()}
        >
          Save wallet fee override
        </Button>
      </Actions>
    </Card>
  );
};

export default CompanyWalletFeeSettings;
